import { LoanOriginationSystemTasksState } from './Types';
import {
  CloseDeleteTaskPopupAction,
  DeleteTaskAction,
  GetTasksSuccessAction,
  OpenDeleteTaskPopupAction,
  SortTasksAction,
  GetTasksAction,
  SavedFiltersSwitch,
} from './ActionCreator';
import { LoanOriginationSystemTasksActionTypes } from './ActionTypes';
import tasksPagination, { TASKS_PER_PAGE_DEFAULT } from 'components/LoanOriginationSystem/TasksDashboard/Pagination';
import { AnyAction } from 'redux';
import { initialState as filtersInitialState, loanOriginationSystemTasksFilterReducer } from './Filters/Reducer';
import { initialState as createTaskInitialState, loanOriginationSystemCreateTaskReducer } from './CreateTask/Reducer';
import { initialState as editTaskInitialState, loanOriginationSystemEditTaskReducer } from './EditTask/Reducer';
import { ChangePopupFiltersAction, FilterTasksByMembersAction, ResetAllTasksFilters } from './Filters/ActionCreator';
import { TasksSortingFields } from 'api/LoanOriginationSystem/LoanOriginationSystemTasksApi';
import { LoanOriginationSystemTasksFiltersActionTypes } from './Filters/ActionTypes';
import withStateReset from 'utils/reducers/withStateReset';
import { setItemsTotal } from 'utils/reducers/setStateField';
import tasksFilterAndSortingSavers from './FilterAndSortingSaver';

type LoanOriginationSystemTasksActions =
  | GetTasksAction
  | GetTasksSuccessAction
  | SortTasksAction
  | OpenDeleteTaskPopupAction
  | CloseDeleteTaskPopupAction
  | DeleteTaskAction
  | ResetAllTasksFilters
  | FilterTasksByMembersAction
  | ChangePopupFiltersAction
  | SavedFiltersSwitch;

type LoanOriginationSystemTasksType = (
  state: LoanOriginationSystemTasksState,
  action: AnyAction,
) => LoanOriginationSystemTasksState;

const getInitialState = (): LoanOriginationSystemTasksState => ({
  items: [],
  page: 1,
  itemsPerPage: TASKS_PER_PAGE_DEFAULT,
  itemsTotal: undefined,
  error: null,
  searched: {},
  filters: { ...filtersInitialState, ...tasksFilterAndSortingSavers.getSavedFilters() },
  sortingType: tasksFilterAndSortingSavers.getSavedSorting() || {
    field: TasksSortingFields.DueDate,
    ascending: true,
  },
  taskIdToDelete: null,
  isDeleteInProgress: false,
  createTask: createTaskInitialState,
  editTask: editTaskInitialState,
});

const loanOriginationSystemTasksReducer = (
  state = getInitialState(),
  action: LoanOriginationSystemTasksActions,
): LoanOriginationSystemTasksState => {
  switch (action.type) {
    case LoanOriginationSystemTasksActionTypes.GetTasks: {
      const { notReload } = action.payload;

      if (notReload) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        itemsTotal: undefined,
      };
    }
    case LoanOriginationSystemTasksActionTypes.GetTasksSuccess: {
      if (action.payload.searchInputValue) {
        return {
          ...state,
          selectedApplicationId: action.payload.applicationId,
          isDeleteInProgress: false,
          taskIdToDelete: null,
          searched: {
            ...state.searched,
            [state.filters.searchInputValue]: {
              items: action.payload.items,
              itemsTotal: action.payload.total,
              page: 1,
              itemsPerPage: TASKS_PER_PAGE_DEFAULT,
              error: '',
            },
          },
        };
      }

      return {
        ...state,
        selectedApplicationId: action.payload.applicationId,
        page: 1,
        itemsPerPage: state?.itemsPerPage || TASKS_PER_PAGE_DEFAULT,
        error: '',
        items: action.payload.items,
        itemsTotal: action.payload.total,
        isDeleteInProgress: false,
        taskIdToDelete: null,
      };
    }

    case LoanOriginationSystemTasksActionTypes.SortTasks: {
      return {
        ...state,
        ...setItemsTotal(state, state.filters.searchInputValue ? ['searched', state.filters.searchInputValue] : []),
        sortingType: action.payload,
      };
    }
    case LoanOriginationSystemTasksActionTypes.OpenDeleteTaskPopup: {
      return {
        ...state,
        taskIdToDelete: action.payload.taskIdToDelete,
      };
    }
    case LoanOriginationSystemTasksActionTypes.CloseDeleteTaskPopup: {
      return {
        ...state,
        taskIdToDelete: null,
      };
    }
    case LoanOriginationSystemTasksActionTypes.DeleteTask: {
      return {
        ...state,
        isDeleteInProgress: true,
      };
    }

    case LoanOriginationSystemTasksFiltersActionTypes.ResetAllFilters:
    case LoanOriginationSystemTasksFiltersActionTypes.FilterTasksByMembers:
    case LoanOriginationSystemTasksFiltersActionTypes.ChangePopupFilters: {
      return {
        ...state,
        ...setItemsTotal(state, state.filters.searchInputValue ? ['searched', state.filters.searchInputValue] : []),
        filters: loanOriginationSystemTasksFilterReducer(state.filters, action),
      };
    }

    case LoanOriginationSystemTasksActionTypes.SavedFiltersSwitch: {
      return {
        ...state,
        sortingType: tasksFilterAndSortingSavers.getSavedSorting() || state.sortingType,
        filters: {
          ...state.filters,
          ...tasksFilterAndSortingSavers.getSavedFilters(),
        },
      };
    }

    default:
      return {
        ...state,
        filters: loanOriginationSystemTasksFilterReducer(state.filters, action),
        createTask: loanOriginationSystemCreateTaskReducer(state.createTask, action),
        editTask: loanOriginationSystemEditTaskReducer(state.editTask, action),
      };
  }
};

export default tasksPagination.wrapReducer<LoanOriginationSystemTasksState>(
  withStateReset(
    loanOriginationSystemTasksReducer,
    LoanOriginationSystemTasksActionTypes.ResetState,
    getInitialState,
  ) as LoanOriginationSystemTasksType,
);
